import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ShadedSelect from '../components/ui/ShadedSelect';

function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
}

const DISTRICT_OPTIONS = [
  'centralAndWestern', 'eastern', 'southern', 'wanChai', 'kowloonCity', 'wongTaiSin',
  'yauTsimMong', 'shamShuiPo', 'islands', 'kwaiTsing', 'north', 'saiKung', 'shaTin',
  'taiPo', 'tsuenWan', 'tuenMun', 'yuenLong', 'kwunTong', 'other'
];

function BetaSchoolAdmin() {
  const { t } = useTranslation();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({ name: '', district: '', email_domains: '' });
  const [submitting, setSubmitting] = useState(false);

  const loadSchools = async () => {
    const res = await fetch('/api/schools', { headers: authHeaders() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to load schools');
    setSchools(data.schools || []);
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await loadSchools();
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setMessage('');
    try {
      const domains = form.email_domains
        .split(/[,\s]+/)
        .map((d) => d.trim().toLowerCase())
        .filter(Boolean);

      const res = await fetch('/api/schools', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          name: form.name.trim(),
          district: form.district,
          email_domains: domains
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to create school');
      setMessage(t('school.schoolCreated'));
      setForm({ name: '', district: '', email_domains: '' });
      await loadSchools();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="max-w-4xl mx-auto px-4 py-12 text-ink-muted">{t('common.loading')}</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
      <h1 className="text-2xl font-bold text-ink">{t('school.betaAdminTitle')}</h1>
      <p className="mt-1 text-sm text-ink-muted">{t('school.betaAdminSubtitle')}</p>

      {error ? <div className="mt-4 rounded-md bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">{error}</div> : null}
      {message ? <div className="mt-4 rounded-md bg-green-50 border border-green-200 text-green-700 px-4 py-3 text-sm">{message}</div> : null}

      <form onSubmit={handleSubmit} className="mt-8 bg-surface border border-edge rounded-lg shadow-sm p-6 space-y-4">
        <h2 className="text-lg font-semibold text-ink">{t('school.createSchool')}</h2>
        <div>
          <label className="block text-sm font-medium text-ink-muted">{t('report.school')}</label>
          <input
            type="text"
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="mt-1 block w-full border border-edge-strong rounded-md px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-ink-muted">{t('common.schoolDistrict')}</label>
          <ShadedSelect
            className="mt-1 block w-full"
            value={form.district}
            onChange={(v) => setForm((f) => ({ ...f, district: v }))}
            options={[
              { value: '', label: t('common.selectSchoolDistrict') },
              ...DISTRICT_OPTIONS.map((value) => ({
                value,
                label: t(`common.districts.${value}`)
              }))
            ]}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-ink-muted">{t('school.emailDomains')}</label>
          <input
            type="text"
            value={form.email_domains}
            onChange={(e) => setForm((f) => ({ ...f, email_domains: e.target.value }))}
            placeholder="school.edu.hk, student.school.edu.hk"
            className="mt-1 block w-full border border-edge-strong rounded-md px-3 py-2 text-sm"
          />
          <p className="mt-1 text-xs text-ink-subtle">{t('school.emailDomainsHelp')}</p>
        </div>
        <button
          type="submit"
          disabled={submitting || !form.name.trim() || !form.district}
          className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary-hover disabled:opacity-50"
        >
          {submitting ? t('common.loading') : t('school.createSchool')}
        </button>
      </form>

      <section className="mt-10">
        <h2 className="text-lg font-semibold text-ink mb-4">{t('school.hostedSchools')}</h2>
        {schools.length === 0 ? (
          <p className="text-sm text-ink-subtle">{t('school.noSchoolsYet')}</p>
        ) : (
          <ul className="space-y-3">
            {schools.map((school) => (
              <li key={school.school_id} className="bg-surface border border-edge rounded-lg p-4">
                <p className="font-medium text-ink">{school.name}</p>
                <p className="text-sm text-ink-subtle">{t(`common.districts.${school.district}`)}</p>
                {school.email_domains?.length ? (
                  <p className="text-xs text-ink-subtle mt-1">{school.email_domains.join(', ')}</p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export default BetaSchoolAdmin;
